/**
 * Helper: Customer
 * ------------------------------------------------------------------------------
 * - Helper to assist with customer related functionality.
 *
 * @namespace Customer
 */
import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * Create a new Customer helper object.
 */
export default () => {
  const customer = mint.customer || {}

  /**
   * Initialise component.
   */
  function init() {

    /**
     * Set initial consent status.
     */
    setCustomerTrackingState()

    setEventBusListeners()
  }

  /**
   * Set Event Bus Listeners
   */
  function setEventBusListeners() {

    /**
     * The user has modified their initial consent status.
     * - Runs when the user presses 'Accept' on the cookie banner.
     */
    mint.EventBus.on('customer:setTrackingConsent', ({ consent }) => {
      setCustomerTrackingState(consent)
    })
  }

  /**
   * Set the new global state for customers.
   * - Other components should leverage this event and object.
   */
  function setCustomerState() {
    mint.customer = {
      ...mint.customer,
      ...customer,
    }

    /**
     * Store in browser - consent is updated whenever Shopify.customerPrivacy
     * is updated. Storage used for Checkout.
     */
    localStorage.setItem(
      storageNamespace.customerTrackingConsent,
      Boolean(customer.canBeTracked),
    )

    mint.EventBus.dispatch('customer:stateUpdated', {
      customer: mint.customer,
    })
  }

  /**
   * Set the state for if a customer can be tracked or not.
   * - If the customer has previously consented, fetch their state from
   *   the browser, else use a fresh parameter from `customer:setTrackingConsent`.
   * @param {Boolean} consent - Updated consent from Shopify event.
   */
  function setCustomerTrackingState(consent) {
    let usersConsent = false

    if (typeof consent === 'boolean') {
      usersConsent = consent
    } else {
      usersConsent = getCustomerTrackingConsent()
    }

    customer.canBeTracked = usersConsent

    setCustomerState()
  }

  /**
   * Explicitly check if the customer has consented to tracking.
   * - For GDPR purposes, the customer MUST consent before being tracked.
   * @returns {Boolean}
   */
  function getCustomerTrackingConsent() {
    let preferences = Boolean(localStorage.getItem(storageNamespace.customerTrackingConsent))

    /**
     * When running in 'development' mode, and the developer has disabled
     * the cookie consent bar from appearing, default to true.
     * - This can be manually toggled during debugging.
     */
    if (mint.flags.dev.disableCookieBanner) {
      preferences = true
    }

    /**
     * Shopify.customerPrivacy doesn't exist in checkout, so fetch
     * previously stored tracking consent.
     */
    if (Shopify.Checkout || Shopify.checkout) {
      preferences = Boolean(
        localStorage.getItem(storageNamespace.customerTrackingConsent),
      )
    }

    return preferences
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
